/**
 * Bank API Service
 * Service layer for vendor bank account management and bank lookup API calls
 *
 * Handles snake_case <-> camelCase transformation between backend and frontend
 *
 * Note: Only /api/banks and /api/banks/branches support the `lang` query parameter.
 * Vendor-specific bank account endpoints (/api/vendors/{id}/banks) do NOT use `lang`.
 *
 * CU-1a masters flip: `list` + `create` + `setDefault` gain a live-module
 * leg (the `masters` key, client.ts) riding VendorBanksCollection /
 * vendorBankAccountAdd / vendorBankAccountSetDefault. The live leg speaks
 * the typed contract directly — the snake_case transform layer is a
 * gold-REST artifact and stays REST-leg-only. update / toggle / bank search
 * / branches / audit-logs stay on the mock seam pending B2.7' completion
 * (register-listed residual).
 */

import apiClient from './axios.config';
import { isLiveModule } from './client';
import { refuseUnobservableMastersWrite } from './mastersWriteReach';
import { GraphQLTransportError, gqlMutate, gqlRequest, throwOnUserErrors } from './graphql.client';
import {
  mastersOperations,
  type VendorBankAccountAddResult,
  type VendorBankAccountSetActiveResult,
  type VendorBankAccountSetDefaultResult,
  type VendorBanksCollectionResult,
} from './masters.operations';
import {
  mapPartyBankAccountNode,
  paginateLegacy,
  toVendorBankAccountAddInput,
} from './masters.gql-map';
import { VENDOR_ROUTES, BANK_ROUTES } from '@/constants/api.constants';
import i18n from '@/i18n/config';
import {
  transformBankAccountResponse,
  transformBankAccountListResponse,
  transformBankAccountRequest,
} from './transforms/bankTransform';
import type {
  BankAccountListResponse,
  BankAccountResponse,
  CreateBankAccountRequest,
  UpdateBankAccountRequest,
  ToggleBankAccountStatusRequest,
  ToggleBankAccountStatusResponse,
  SetDefaultBankAccountRequest,
  SetDefaultBankAccountResponse,
  BankAccountListParams,
  BankSearchResponse,
  BankSearchParams,
  BranchSearchResponse,
  BranchSearchParams,
  BankAccountAuditLogResponse,
  BankAccountAuditLogParams,
} from '@/types/api/bank';

/**
 * Get API language code from current UI language
 * API only accepts 'en' or 'zh', not regional variants like 'zh-TW'
 */
const getApiLanguage = (): 'en' | 'zh' => {
  return i18n.language.startsWith('zh') ? 'zh' : 'en';
};

/**
 * Bank Account API service object
 */
export const bankApi = {
  /**
   * GET /api/vendors/:vendorId/banks
   * Fetch list of bank accounts for a vendor
   */
  list: async (
    vendorId: number,
    params?: BankAccountListParams
  ): Promise<BankAccountListResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlRequest<VendorBanksCollectionResult>(
        mastersOperations.vendorBanksCollection,
        { id: String(vendorId) }
      );
      if (!result.vendor) {
        throw new GraphQLTransportError({
          message: `Vendor ${vendorId} not found`,
          graphQLErrors: ['NOT_FOUND'],
          status: 404,
        });
      }
      return paginateLegacy(
        result.vendor.bankAccounts.map(mapPartyBankAccountNode),
        params?.page,
        params?.pageSize
      );
    }
    const response = await apiClient.get(
      VENDOR_ROUTES.BANKS(vendorId),
      { params }
    );
    return transformBankAccountListResponse(response.data);
  },

  /**
   * POST /api/vendors/:vendorId/banks
   * Create a new bank account for a vendor
   */
  create: async (
    vendorId: number,
    data: Omit<CreateBankAccountRequest, 'vendorId'>
  ): Promise<BankAccountResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlMutate<VendorBankAccountAddResult>(
        mastersOperations.vendorBankAccountAdd,
        { input: toVendorBankAccountAddInput(vendorId, data) }
      );
      throwOnUserErrors(result.vendorBankAccountAdd.userErrors, 'vendorBankAccountAdd');
      if (!result.vendorBankAccountAdd.bankAccount) {
        throw new GraphQLTransportError({
          message: 'vendorBankAccountAdd returned neither an account nor userErrors',
          graphQLErrors: ['EMPTY_PAYLOAD'],
        });
      }
      return mapPartyBankAccountNode(result.vendorBankAccountAdd.bankAccount);
    }
    // Transform camelCase to snake_case for backend
    const requestData = transformBankAccountRequest({
      ...data,
      vendorId,
    });

    const response = await apiClient.post(
      VENDOR_ROUTES.BANKS(vendorId),
      requestData
    );
    // Transform snake_case response to camelCase
    return transformBankAccountResponse(response.data);
  },

  /**
   * PUT /api/vendors/:vendorId/banks/:bankId
   * Update an existing bank account
   */
  update: async (
    vendorId: number,
    bankId: number,
    data: UpdateBankAccountRequest
  ): Promise<BankAccountResponse> => {
    refuseUnobservableMastersWrite('bankApi.update');
    // Transform camelCase to snake_case for backend
    const requestData = transformBankAccountRequest(data);

    const response = await apiClient.put(
      `${VENDOR_ROUTES.BANKS(vendorId)}/${bankId}`,
      requestData
    );
    // Transform snake_case response to camelCase
    return transformBankAccountResponse(response.data);
  },

  /**
   * PATCH /api/vendors/:vendorId/banks/:bankId/toggle
   * Toggle bank account active status
   *
   * LIVE (CU-1b masters activation) — `vendorBankAccountSetActive`, the
   * boolean travelling. Customers have no bank-account facade at all, so
   * `customerBankAccountSetActive` (declared by the SDL) has no call site
   * here and deliberately got no document.
   */
  toggleStatus: async (
    vendorId: number,
    bankId: number,
    isActive: boolean
  ): Promise<ToggleBankAccountStatusResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlMutate<VendorBankAccountSetActiveResult>(
        mastersOperations.vendorBankAccountSetActive,
        { input: { vendorId: String(vendorId), bankAccountId: bankId, isActive } }
      );
      throwOnUserErrors(
        result.vendorBankAccountSetActive.userErrors,
        'vendorBankAccountSetActive'
      );
      if (!result.vendorBankAccountSetActive.bankAccount) {
        throw new GraphQLTransportError({
          message: 'vendorBankAccountSetActive returned neither an account nor userErrors',
          graphQLErrors: ['EMPTY_PAYLOAD'],
        });
      }
      // Same declared-vs-wire mismatch `setDefault` documents above: the
      // declared ToggleBankAccountStatusResponse ({message, bankId, isActive})
      // never matched the wire — mock echoes the FULL account row and
      // `useBankMutations` puts the response straight into the store. Serving
      // the full row keeps the wire twin; the type fix is a B0.3' register row.
      return mapPartyBankAccountNode(
        result.vendorBankAccountSetActive.bankAccount
      ) as unknown as ToggleBankAccountStatusResponse;
    }
    const response = await apiClient.patch<ToggleBankAccountStatusResponse>(
      `${VENDOR_ROUTES.BANKS(vendorId)}/${bankId}/toggle`,
      { isActive } as ToggleBankAccountStatusRequest
    );
    return response.data;
  },

  /**
   * PATCH /api/vendors/:vendorId/banks/:bankId/set-default
   * Set bank account as default
   */
  setDefault: async (
    vendorId: number,
    bankId: number,
    isDefault: boolean
  ): Promise<SetDefaultBankAccountResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlMutate<VendorBankAccountSetDefaultResult>(
        mastersOperations.vendorBankAccountSetDefault,
        { input: { vendorId: String(vendorId), bankAccountId: bankId, isDefault } }
      );
      throwOnUserErrors(
        result.vendorBankAccountSetDefault.userErrors,
        'vendorBankAccountSetDefault'
      );
      if (!result.vendorBankAccountSetDefault.bankAccount) {
        throw new GraphQLTransportError({
          message: 'vendorBankAccountSetDefault returned neither an account nor userErrors',
          graphQLErrors: ['EMPTY_PAYLOAD'],
        });
      }
      // The declared SetDefaultBankAccountResponse ({message, bankId:string,
      // isDefault}) never matched the wire — mock and gold echo the FULL
      // account row, and callers read the account fields. Serving the full
      // row keeps the wire twin; the type fix is a B0.3' register row.
      return mapPartyBankAccountNode(
        result.vendorBankAccountSetDefault.bankAccount
      ) as unknown as SetDefaultBankAccountResponse;
    }
    const response = await apiClient.patch<SetDefaultBankAccountResponse>(
      `${VENDOR_ROUTES.BANKS(vendorId)}/${bankId}/set-default`,
      { isDefault } as SetDefaultBankAccountRequest
    );
    return response.data;
  },

  /**
   * GET /api/banks
   * Search for banks by country and bank code
   * Supports `lang` parameter per API spec
   */
  searchBanks: async (params: BankSearchParams): Promise<BankSearchResponse> => {
    const response = await apiClient.get<BankSearchResponse>(BANK_ROUTES.SEARCH, {
      params: { ...params, lang: params.lang ?? getApiLanguage() },
    });
    return response.data;
  },

  /**
   * GET /api/banks/branches
   * Search for bank branches
   * Supports `lang` parameter per API spec
   */
  searchBranches: async (params: BranchSearchParams): Promise<BranchSearchResponse> => {
    const response = await apiClient.get<BranchSearchResponse>(BANK_ROUTES.BRANCHES, {
      params: { ...params, lang: params.lang ?? getApiLanguage() },
    });
    return response.data;
  },

  /**
   * GET /api/vendors/:vendorId/banks/:bankId/audit-logs
   * Fetch audit logs for a specific bank account
   */
  getAuditLogs: async (
    vendorId: number,
    bankId: number,
    params?: BankAccountAuditLogParams
  ): Promise<BankAccountAuditLogResponse> => {
    const response = await apiClient.get<BankAccountAuditLogResponse>(
      `${VENDOR_ROUTES.BANKS(vendorId)}/${bankId}/audit-logs`,
      { params }
    );
    return response.data;
  },
};

export default bankApi;
